import TranslateIcon from '@mui/icons-material/Translate';
import { Box, ButtonBase, Chip, InputAdornment, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { TextField, type TextFieldProps } from './text-field';
import { tr } from '../lib/i18n';

/**
 * Bilingual content for everything the platform publishes to companies.
 *
 * UX: ONE input per text with a small "ع | EN" switch inside it (not two inputs side by side).
 * - Switching the language in any field switches every field under the same <BilingualScope>,
 *   so the admin writes all Arabic first, flips once to EN, and translates top to bottom.
 * - On EN the Arabic text is shown as the placeholder (translate while looking at it).
 * - A red dot marks the language that is still empty; the scope shows how many are missing.
 */
export type Lang = 'ar' | 'en';
export interface Bilingual {
  ar: string;
  en: string;
}

export const emptyBilingual = (): Bilingual => ({ ar: '', en: '' });
/** From DB columns (name_ar / name_en) or a Localized JSON object. */
export const bi = (ar?: string | null, en?: string | null): Bilingual => ({ ar: ar ?? '', en: en ?? '' });
export const fromLocalized = (l?: { ar?: string; en?: string } | null): Bilingual => bi(l?.ar, l?.en);
/** To a Localized JSON object (form texts); undefined when both are empty. */
export const toLocalized = (v: Bilingual): { ar: string; en?: string } | undefined => {
  const ar = v.ar.trim();
  const en = v.en.trim();
  if (!ar && !en) return undefined;
  return en ? { ar, en } : { ar };
};
export const isComplete = (v: Bilingual) => !!v.ar.trim() && !!v.en.trim();
/** Filled in one language but not the other (an optional text left fully empty is fine). */
export const isHalfFilled = (v: Bilingual) => !!v.ar.trim() !== !!v.en.trim();
export const missingLang = (v: Bilingual, required = true): Lang | null => {
  if (!v.ar.trim() && (required || v.en.trim())) return 'ar';
  if (!v.en.trim() && (required || v.ar.trim())) return 'en';
  return null;
};

// ---------------------------------------------------------------------------
// scope: one shared editing language for a dialog / page
// ---------------------------------------------------------------------------
const ScopeCtx = createContext<{ lang: Lang; setLang: (l: Lang) => void } | null>(null);

export function BilingualScope({ children, initial = 'ar' }: { children: ReactNode; initial?: Lang }) {
  const [lang, setLang] = useState<Lang>(initial);
  const value = useMemo(() => ({ lang, setLang }), [lang]);
  return <ScopeCtx.Provider value={value}>{children}</ScopeCtx.Provider>;
}

/** Current editing language (shared when inside a scope, otherwise local to the field). */
function useLang(): [Lang, (l: Lang) => void] {
  const ctx = useContext(ScopeCtx);
  const [local, setLocal] = useState<Lang>('ar');
  return ctx ? [ctx.lang, ctx.setLang] : [local, setLocal];
}

/** Exposes the scope so a dialog can jump to the language that is missing on save. */
export function useBilingualScope() {
  return useContext(ScopeCtx);
}

const Dot = ({ color }: { color: string }) => (
  <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, display: 'inline-block', flexShrink: 0 }} />
);

function LangSwitch({ value, lang, onLang, required, disabled }: { value: Bilingual; lang: Lang; onLang: (l: Lang) => void; required: boolean; disabled?: boolean }) {
  const item = (l: Lang, text: string) => {
    const empty = !value[l].trim();
    const otherFilled = !!value[l === 'ar' ? 'en' : 'ar'].trim();
    const warn = empty && (required || otherFilled);
    const active = lang === l;
    return (
      <Tooltip title={warn ? (l === 'ar' ? tr('النص العربي ناقص', 'Arabic text is missing') : tr('النص الإنجليزي ناقص', 'English text is missing')) : l === 'ar' ? 'العربية' : 'English'}>
        <ButtonBase
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault() /* keep the caret in the input */}
          onClick={() => onLang(l)}
          aria-pressed={active}
          aria-label={l === 'ar' ? tr('تحرير النص العربي', 'Edit Arabic text') : tr('تحرير النص الإنجليزي', 'Edit English text')}
          sx={(t) => ({
            gap: 0.5,
            px: 1,
            height: 26,
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1,
            color: active ? t.palette.primary.contrastText : t.palette.text.secondary,
            bgcolor: active ? t.palette.primary.main : 'transparent',
            transition: 'background-color 120ms, color 120ms',
            '&:hover': { bgcolor: active ? t.palette.primary.main : alpha(t.palette.text.primary, 0.08) },
          })}
        >
          {text}
          {warn && <Dot color="error.main" />}
        </ButtonBase>
      </Tooltip>
    );
  };
  return (
    <Stack
      direction="row"
      spacing={0.25}
      sx={{ p: 0.25, borderRadius: 999, border: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}
      dir="ltr"
    >
      {item('ar', 'ع')}
      {item('en', 'EN')}
    </Stack>
  );
}

export type BilingualFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'defaultValue'> & {
  value: Bilingual;
  onChange: (v: Bilingual) => void;
  /** both languages required (default). false = optional, but never half-filled */
  required?: boolean;
  /** server / form error for the Arabic or English part */
  errorAr?: string;
  errorEn?: string;
};

/**
 * One text input that holds Arabic + English.
 * Use inside <BilingualScope> so every field flips language together.
 */
export function BilingualField({ value, onChange, required = true, errorAr, errorEn, label, helperText, slotProps, disabled, ...props }: BilingualFieldProps) {
  const [lang, setLang] = useLang();
  const other: Lang = lang === 'ar' ? 'en' : 'ar';
  const otherMissing = !value[other].trim() && (required || !!value[lang].trim());
  const err = lang === 'ar' ? errorAr : errorEn;
  const otherErr = lang === 'ar' ? errorEn : errorAr;

  const hint = err
    ?? (otherErr ? `${other === 'en' ? tr('الإنجليزي', 'English') : tr('العربي', 'Arabic')}: ${otherErr}` : undefined)
    ?? (otherMissing && value[lang].trim() ? (other === 'en' ? tr('ينقصه النص الإنجليزي — اضغط EN للترجمة', 'English text missing — click EN to translate') : tr('ينقصه النص العربي — اضغط ع', 'Arabic text missing — click ع')) : undefined)
    ?? helperText;

  const inputSlot = (slotProps?.input ?? {}) as Record<string, unknown>;
  const htmlSlot = (slotProps?.htmlInput ?? {}) as Record<string, unknown>;

  return (
    <TextField
      {...props}
      disabled={disabled}
      label={
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          {label}
          <Typography component="span" variant="caption" sx={{ opacity: 0.75 }}>
            {lang === 'ar' ? tr('(عربي)', '(Arabic)') : '(English)'}
          </Typography>
        </Box>
      }
      required={required}
      value={value[lang]}
      onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
      error={!!err || !!otherErr || props.error}
      helperText={hint ?? ' '}
      placeholder={lang === 'en' && value.ar ? value.ar : props.placeholder}
      slotProps={{
        ...slotProps,
        formHelperText: { sx: { color: !err && !otherErr && otherMissing && value[lang].trim() ? 'warning.main' : undefined } },
        htmlInput: { ...htmlSlot, dir: lang === 'ar' ? 'rtl' : 'ltr', lang },
        input: {
          ...inputSlot,
          endAdornment: (
            <InputAdornment position="end" sx={{ alignSelf: props.multiline ? 'flex-start' : undefined, mt: props.multiline ? 1 : undefined }}>
              <LangSwitch value={value} lang={lang} onLang={setLang} required={required} disabled={disabled} />
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

/** Header control for a scope: switch language for all fields + how many translations are missing. */
export function BilingualToolbar({ values = [], counts, sx }: { values?: { value: Bilingual; required?: boolean }[]; counts?: { ar: number; en: number }; sx?: object }) {
  const ctx = useContext(ScopeCtx);
  const missingEn = counts?.en ?? values.filter((v) => !v.value.en.trim() && (v.required !== false || !!v.value.ar.trim())).length;
  const missingAr = counts?.ar ?? values.filter((v) => !v.value.ar.trim() && (v.required !== false || !!v.value.en.trim())).length;
  if (!ctx) return null;
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: 'action.hover', ...sx }}>
      <TranslateIcon fontSize="small" color="action" />
      <Typography variant="body2" sx={{ flex: 1 }}>
        {missingAr + missingEn === 0
          ? tr('النصوص جاهزة باللغتين', 'All texts are complete in both languages')
          : [missingAr && tr(`${missingAr} بدون عربي`, `${missingAr} missing Arabic`), missingEn && tr(`${missingEn} بدون إنجليزي`, `${missingEn} missing English`)].filter(Boolean).join(' — ')}
      </Typography>
      <LangSwitch value={{ ar: missingAr ? '' : 'x', en: missingEn ? '' : 'x' }} lang={ctx.lang} onLang={ctx.setLang} required />
    </Stack>
  );
}

/** Table / list cell: Arabic on top, English below (or a "missing" chip). */
export function BilingualText({ ar, en, missingLabel = tr('بدون إنجليزي', 'No English'), dense }: { ar: ReactNode; en?: string | null; missingLabel?: string; dense?: boolean }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" noWrap={dense} sx={{ fontWeight: 500 }}>{ar}</Typography>
      {en?.trim() ? (
        <Typography variant="caption" color="text.secondary" component="div" noWrap={dense}>
          <bdi>{en}</bdi>
        </Typography>
      ) : (
        <Chip size="small" color="warning" variant="outlined" label={missingLabel} sx={{ mt: 0.25, height: 20, fontSize: 11 }} />
      )}
    </Box>
  );
}
